<template>
  <div class="karvata-dovoz-page">
    <div class="header-main">
      <div class="hm-left">
        <div class="header-main__title-wrap">
          <h1 class="header-main__title">Довоз с Карваты</h1>
        </div>
      </div>
    </div>

    <div class="panel">
      <p class="desc">
        Автодовоз v2: до трёх перемещений в МойСклад —
        «Довоз продажи», «Довоз продажи (без порога &gt;10)» и «Довоз неликвид».
        Документы создаются непроведёнными — их нужно проверить и провести вручную.
      </p>

      <div class="form-row">
        <label class="form-label" for="shop-select">Магазин</label>
        <select
          id="shop-select"
          v-model="selectedShopKey"
          class="form-select"
          :disabled="loadingStores || running"
        >
          <option value="">— выберите магазин —</option>
          <optgroup label="Минск">
            <option
              v-for="shop in minskStores"
              :key="shop.key"
              :value="shop.key"
            >
              {{ shop.name }}
            </option>
          </optgroup>
          <optgroup label="Регион">
            <option
              v-for="shop in regionalStores"
              :key="shop.key"
              :value="shop.key"
            >
              {{ shop.name }}
            </option>
          </optgroup>
        </select>
      </div>

      <div class="form-row checkbox-row">
        <label class="checkbox-label">
          <input
            v-model="dryRun"
            type="checkbox"
            :disabled="running"
          />
          Только предпросмотр (без создания документов)
        </label>
      </div>

      <div class="actions">
        <button
          class="btn-primary"
          :disabled="!selectedShopKey || running"
          @click="runDovoz"
        >
          {{ running ? 'Формирование…' : 'Сформировать довоз' }}
        </button>
        <button
          class="btn-secondary"
          :disabled="loadingStores || running"
          @click="loadStores"
        >
          Обновить список
        </button>
      </div>

      <p v-if="error" class="error-msg">{{ error }}</p>
    </div>

    <div v-if="result" class="result-panel">
      <h2 class="result-title">
        {{ result.dry_run ? 'Предпросмотр' : 'Довоз создан' }}: {{ result.shop_name }}
      </h2>

      <div class="stats">
        <div class="stat">
          <span class="stat-label">Продажи (&gt;10)</span>
          <span class="stat-value">{{ result.move_sales_positions_count || 0 }}</span>
        </div>
        <div class="stat">
          <span class="stat-label">Продажи без порога (все п.1/п.2)</span>
          <span class="stat-value">{{ result.move_sales_no_min_positions_count || 0 }}</span>
        </div>
        <div class="stat">
          <span class="stat-label">Неликвид</span>
          <span class="stat-value">{{ result.move_neliquid_positions_count || 0 }}</span>
        </div>
        <div class="stat">
          <span class="stat-label">Всего</span>
          <span class="stat-value">{{ result.move_positions_count || 0 }}</span>
        </div>
        <div v-if="result.duration_sec" class="stat">
          <span class="stat-label">Время</span>
          <span class="stat-value">{{ result.duration_sec }} с</span>
        </div>
      </div>

      <div v-if="result.last_move && result.last_move.name" class="info-line">
        Последний ручной довоз: <strong>{{ result.last_move.name }}</strong>
        <span v-if="result.last_move.moment"> ({{ result.last_move.moment }})</span>
      </div>

      <div v-if="!result.dry_run && (result.move_sales || result.move_sales_no_min || result.move_neliquid)" class="moves">
        <div v-if="result.move_sales" class="move-card">
          <div class="move-label">Довоз продажи</div>
          <a
            v-if="result.move_sales.uuidHref"
            :href="result.move_sales.uuidHref"
            target="_blank"
            rel="noopener"
            class="move-link"
          >
            {{ result.move_sales.name || 'Открыть в МойСклад' }}
          </a>
          <span v-else>{{ result.move_sales.name || '—' }}</span>
        </div>
        <div v-if="result.move_sales_no_min" class="move-card">
          <div class="move-label">Довоз продажи (без порога &gt;10)</div>
          <a
            v-if="result.move_sales_no_min.uuidHref"
            :href="result.move_sales_no_min.uuidHref"
            target="_blank"
            rel="noopener"
            class="move-link"
          >
            {{ result.move_sales_no_min.name || 'Открыть в МойСклад' }}
          </a>
          <span v-else>{{ result.move_sales_no_min.name || '—' }}</span>
        </div>
        <div v-if="result.move_neliquid" class="move-card">
          <div class="move-label">Довоз неликвид</div>
          <a
            v-if="result.move_neliquid.uuidHref"
            :href="result.move_neliquid.uuidHref"
            target="_blank"
            rel="noopener"
            class="move-link"
          >
            {{ result.move_neliquid.name || 'Открыть в МойСклад' }}
          </a>
          <span v-else>{{ result.move_neliquid.name || '—' }}</span>
        </div>
      </div>

      <div v-if="result.message" class="info-line muted">{{ result.message }}</div>

      <details v-if="result.case1_items && result.case1_items.length" class="details-block">
        <summary>Довоз продажи — П.1 ({{ result.case1_items.length }})</summary>
        <ul>
          <li v-for="(item, i) in result.case1_items" :key="'c1-' + i">
            {{ item.name }} — {{ item.move_qty }} шт.
          </li>
        </ul>
      </details>

      <details v-if="result.case2_items && result.case2_items.length" class="details-block">
        <summary>Довоз продажи — П.2 ({{ result.case2_items.length }})</summary>
        <ul>
          <li v-for="(item, i) in result.case2_items" :key="'c2-' + i">
            {{ item.name }} — {{ item.move_qty }} шт.
          </li>
        </ul>
      </details>

      <details v-if="case3SalesItems.length" class="details-block">
        <summary>Довоз продажи (без порога &gt;10) — все п.1/п.2 ({{ case3SalesItems.length }})</summary>
        <ul>
          <li v-for="(item, i) in case3SalesItems" :key="'c3s-' + i">
            {{ item.name }} — {{ item.move_qty }} шт. (на Карвате {{ item.karvata_stock }})
          </li>
        </ul>
      </details>

      <details v-if="result.case3_items && result.case3_items.length" class="details-block">
        <summary>Довоз неликвид ({{ result.case3_items.length }})</summary>
        <ul>
          <li v-for="(item, i) in result.case3_items" :key="'c3-' + i">
            {{ item.name }} — {{ item.move_qty }} шт.
          </li>
        </ul>
      </details>
    </div>
  </div>
</template>

<script>
import { ORDERS_API_BASE } from '@/config/api';
import axios from 'axios';

const API_URL = ORDERS_API_BASE + '/karvata_dovoz.php';

export default {
  name: 'KarvataDovozPage',
  data() {
    return {
      stores: [],
      selectedShopKey: '',
      dryRun: false,
      loadingStores: false,
      running: false,
      error: null,
      result: null,
    };
  },
  computed: {
    minskStores() {
      return this.stores.filter((s) => s.region === 'minsk');
    },
    regionalStores() {
      return this.stores.filter((s) => s.region === 'regional');
    },
    case3SalesItems() {
      if (!this.result) return [];
      return this.result.case3_sales_items
        || this.result.sold_low_karvata
        || [];
    },
  },
  mounted() {
    this.loadStores();
  },
  methods: {
    async loadStores() {
      this.loadingStores = true;
      this.error = null;
      try {
        const { data } = await axios.get(API_URL);
        if (!data.success) {
          throw new Error(data.message || 'Не удалось загрузить магазины');
        }
        this.stores = data.stores || [];
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.message)
          || e.message
          || 'Ошибка загрузки магазинов';
        this.stores = [];
      } finally {
        this.loadingStores = false;
      }
    },
    async runDovoz() {
      if (!this.selectedShopKey || this.running) return;
      this.running = true;
      this.error = null;
      this.result = null;
      try {
        const { data } = await axios.post(API_URL, {
          shop_key: this.selectedShopKey,
          dry_run: this.dryRun,
        }, { timeout: 300000 });
        if (!data.success) {
          throw new Error(data.message || 'Ошибка формирования довоза');
        }
        this.result = data.result || null;
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.message)
          || e.message
          || 'Ошибка формирования довоза';
      } finally {
        this.running = false;
      }
    },
  },
};
</script>

<style scoped>
.karvata-dovoz-page {
  padding: 16px 20px 40px;
  max-width: 900px;
}

.desc {
  margin: 0 0 20px;
  color: #555;
  line-height: 1.5;
}

.panel,
.result-panel {
  background: #fff;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.form-row {
  margin-bottom: 16px;
}

.form-label {
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
}

.form-select {
  width: 100%;
  max-width: 420px;
  padding: 8px 10px;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.checkbox-row {
  margin-bottom: 20px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.btn-primary,
.btn-secondary {
  padding: 10px 18px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-size: 14px;
}

.btn-primary {
  background: #005bff;
  color: #fff;
}

.btn-primary:disabled,
.btn-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: #f0f0f0;
  color: #333;
}

.error-msg {
  margin-top: 16px;
  color: #c0392b;
}

.result-title {
  margin: 0 0 16px;
  font-size: 18px;
}

.stats {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
}

.stat {
  background: #f7f9fc;
  border-radius: 6px;
  padding: 10px 14px;
  min-width: 120px;
}

.stat-label {
  display: block;
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 20px;
  font-weight: 700;
}

.info-line {
  margin-bottom: 12px;
}

.info-line.muted {
  color: #666;
}

.moves {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 16px 0;
}

.move-card {
  padding: 12px;
  background: #f0f7ff;
  border-radius: 6px;
}

.move-label {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
}

.move-link {
  color: #005bff;
  font-weight: 600;
  text-decoration: none;
}

.move-link:hover {
  text-decoration: underline;
}

.details-block {
  margin-top: 12px;
}

.details-block summary {
  cursor: pointer;
  font-weight: 600;
  margin-bottom: 8px;
}

.details-block ul {
  margin: 0;
  padding-left: 20px;
}

.details-block li {
  margin-bottom: 4px;
}
</style>
